<template>
  <div class="tnm-selector-wrapper">
    <!-- Selected Path Display (Breadcrumb) -->
    <div v-if="branchId && !showSelection" class="selected-path-section">
      <div class="path-header">
        <h3 class="section-title">Assessment</h3>
        <button class="btn-backd" @click="editTnm" aria-label="Edit">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
          </svg>
        </button>
        <button class="btn-edit" @click="editTnm">
          <img
            src="../assets/editing.png"
            alt="Edit"
            class="btn-icon"
            style="width: 20px; height: 20px;"
          />
        </button>
      </div>

      <!-- Breadcrumb Path -->
      <div class="breadcrumb-container">
        <div class="breadcrumb-path">
          <template v-for="(item, index) in selectedPathLabels">
            <span :key="'label-' + index" class="breadcrumb-item">{{ item }}</span>
            <svg
              v-if="index !== selectedPathLabels.length - 1"
              :key="'divider-' + index"
              class="breadcrumb-divider"
              viewBox="0 0 12 24"
              fill="currentColor"
            >
              <path d="M3 5l7 7-7 7"/>
            </svg>
          </template>
        </div>
      </div>
    </div>
    <div v-if="selectedType || selectedTumour || selectedNode || selectedMetastases" class="tnm-summary">
    <div class="summary-header">
      <h5>Current Selection:</h5>
    </div>
    <div class="summary-path">
      <span v-if="selectedType" class="summary-badge">{{ selectedType }}</span>
      <span v-if="selectedTumour" class="summary-badge">{{ selectedTumour }}</span>
      <span v-if="selectedNode" class="summary-badge">{{ selectedNode }}</span>
      <span v-if="selectedMetastases" class="summary-badge">{{ selectedMetastases }}</span>
    </div>
  </div>
    <!-- Disease Selection Interface -->
    <div v-if="showSelection">
      <!-- Welcome Header - Only show when at root level -->
      <div v-if="navigationStack.length === 0 && !isOncologyFlow" class="welcome-header">
        <p class="welcome-text">Welcome to</p>
        <div class="logo-container">
          <img src="../assets/ProxyMed_Logo_RGB.svg" alt="ProxyMed" class="welcome-logo">
        </div>
        <p class="welcome-subtitle">Your personal trainer for clinical decision-making</p>
      </div>

      <!-- ONCOLOGY TNM STAGING FLOW - SINGLE SCREEN -->
      <div v-if="isOncologyFlow" class="navigation-card">
        <div class="card-header-row">
          <div class="current-step-info">
            <h4 class="selection-title">Lung Cancer - TNM Staging</h4>
            
          </div>
          
          <button class="btn-back" @click="exitOncologyFlow">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M15 19l-7-7 7-7"/>
            </svg>
            <span>Back</span>
          </button>
        </div>

        <!-- Single Screen TNM Form -->
        <div class="tnm-single-screen">
  
  <!-- Step 1: Cancer Type -->
  <div class="tnm-section-single">
    <div class="tnm-section-header-inline">
      <div class="tnm-label-group">
        <span class="step-badge">1</span>
        <h5 class="tnm-step-title">Cancer Type</h5>
      </div>
      <div class="tnm-button-group">
        <button
          v-for="type in types"
          :key="type"
          @click="selectedType = type"
          class="tnm-option-btn"
          :class="{ 'tnm-selected': selectedType === type }"
        >
          {{ type }}
        </button>
      </div>
    </div>
  </div>

  <!-- Step 2: Tumor Stage -->
  <div class="tnm-section-single" :class="{ 'tnm-disabled': !selectedType }">
    <div class="tnm-section-header-inline">
      <div class="tnm-label-group">
        <span class="step-badge" :class="{ 'badge-disabled': !selectedType }">2</span>
        <h5 class="tnm-step-title">Tumor Stage</h5>
      </div>
      <div class="tnm-button-group">
        <button
          v-for="tumour in tumours"
          :key="tumour"
          @click="selectedTumour = tumour"
          :disabled="!selectedType"
          class="tnm-option-btn"
          :class="{ 'tnm-selected': selectedTumour === tumour }"
        >
          {{ tumour }}
        </button>
      </div>
    </div>
  </div>

  <!-- Step 3: Node Stage -->
  <div class="tnm-section-single" :class="{ 'tnm-disabled': !selectedTumour }">
    <div class="tnm-section-header-inline">
      <div class="tnm-label-group">
        <span class="step-badge" :class="{ 'badge-disabled': !selectedTumour }">3</span>
        <h5 class="tnm-step-title">Node Stage</h5>
      </div>
      <div class="tnm-button-group">
        <button
          v-for="node in nodes"
          :key="node"
          @click="selectedNode = node"
          :disabled="!selectedTumour"
          class="tnm-option-btn"
          :class="{ 'tnm-selected': selectedNode === node }"
        >
          {{ node }}
        </button>
      </div>
    </div>
  </div>

  <!-- Step 4: Metastases Stage -->
  <div class="tnm-section-single" :class="{ 'tnm-disabled': !selectedNode }">
    <div class="tnm-section-header-inline">
      <div class="tnm-label-group">
        <span class="step-badge" :class="{ 'badge-disabled': !selectedNode }">4</span>
        <h5 class="tnm-step-title">Metastases Stage</h5>
      </div>
      <div class="tnm-button-group">
        <button
          v-for="metastasesInfo in metastases"
          :key="metastasesInfo"
          @click="selectedMetastases = metastasesInfo"
          :disabled="!selectedNode"
          class="tnm-option-btn"
          :class="{ 'tnm-selected': selectedMetastases === metastasesInfo }"
        >
          {{ metastasesInfo }}
        </button>
      </div>
    </div>
  </div>

  <!-- Extra Questions (if any) -->
  <div v-if="extraQuestions.length > 0" class="tnm-section-single">
    <div class="tnm-section-header-inline">
      <div class="tnm-label-group">
        <span class="step-badge">5</span>
        <h5 class="tnm-step-title">Additional Information</h5>
      </div>
    </div>
    
    <div v-for="(questions, index) in extraQuestions" :key="index" class="extra-questions-single">
      <p class="extra-question-label">{{ questions.parentId }}</p>
      <div class="tnm-button-group">
        <button
          v-for="entry in questions.entries"
          :key="entry.id"
          @click="selectResult(questions.id, entry.id, index)"
          class="tnm-option-btn"
        >
          {{ entry.name }}
        </button>
      </div>
    </div>
  </div>

  <!-- Summary & Continue -->
  
</div>
      </div>

      <!-- TREE NAVIGATION (NON-ONCOLOGY) -->
      <div v-if="!isOncologyFlow" class="navigation-card">
        <div class="card-header-row">
          <div class="current-step-info">
            <h4 class="selection-title">
              {{ currentGroup?.nextParameterTitle || currentGroup?.parameterText }}
            </h4>
          </div>
          
          <button
            v-if="navigationStack.length"
            class="btn-back"
            @click="goBackInGroup"
            aria-label="Back"
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M15 19l-7-7 7-7"/>
            </svg>
            <span class="btn-back-label">Back</span>
          </button>
        </div>

        <div class="options-grid">
          <button
            v-for="item in currentGroup?.nextParameterItems"
            :key="item.id"
            @click.stop="handleOptionClick(item)"
            class="option-button"
            :class="{ 
              'option-branch': item.type === 'branch',
              'option-disabled': isPremiumLocked(item),
              'option-premium-accessible': isPremiumAccessible(item)
            }"
          >
            <div class="option-icon" :class="{ 'icon-branch': item.type === 'branch' }">
              <img 
                v-if="item.iconPath" 
                :src="getIconPath(item.iconPath)" 
                :alt="item.parameterText"
              />
              <img 
                v-else 
                src="../assets/icons/stethoscope.png" 
                alt="Default icon"
              />
            </div>
            <span class="option-text">{{ item.parameterText }}</span>
            
            <!-- PRO Badge -->
            <template v-if="isPremiumItem(item)">
              <span 
                class="pro-badge" 
                :class="{ 'pro-badge-accessible': isPremiumAccessible(item) }"
              >
                PRO
              </span>
              <!-- Lock Icon ONLY for locked items -->
              <svg 
                v-if="isPremiumLocked(item)"
                class="lock-icon" 
                viewBox="0 0 24 24" 
                fill="currentColor"
              >
                <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/>
              </svg>
            </template>
          </button>
        </div>
      </div>
    </div>

    <!-- Pro Upgrade Modal -->
    <div v-if="showProModal" class="pro-modal-overlay" @click="closeProModal">
      <div class="pro-modal" @click.stop>
        <button class="modal-close" @click="closeProModal">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
          </svg>
        </button>
        
        <div class="modal-icon">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/>
          </svg>
        </div>
        
        <h3 class="modal-title">Upgrade to Pro</h3>
        <p class="modal-subtitle">
          Unlock all healthcare specialties and advanced features with our Pro plan
        </p>
        
        <div class="modal-features">
          <div class="feature-item">
            <svg class="feature-check" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
            </svg>
            <span class="feature-text">Access all medical fields</span>
          </div>
          <div class="feature-item">
            <svg class="feature-check" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
            </svg>
            <span class="feature-text">Advanced AI recommendations</span>
          </div>
          <div class="feature-item">
            <svg class="feature-check" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
            </svg>
            <span class="feature-text">Priority support 24/7</span>
          </div>
          <div class="feature-item">
            <svg class="feature-check" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
            </svg>
            <span class="feature-text">Custom digital twins</span>
          </div>
        </div>
        
        <button class="btn-upgrade" @click="closeProModal">
          Upgrade Now
        </button>
        
        <button class="btn-later" @click="closeProModal">
          Maybe Later
        </button>
      </div>
    </div>

    <!-- Doctor Selection Modal -->
    <DoctorSelectionModal 
      v-if="showDoctorModal"
      :showModal="showDoctorModal"
      :branchId="pendingBranchId || localBranchId"
      :departmentType="selectedDepartmentType"
      @doctor-selected="onDoctorSelected"
      @modal-closed="closeDoctorModal"
    />
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { ResultInfo, TnmModelTest } from "@/entities/TnmModel";
import { HttpHandler } from "@/http/HttpHandler";
import { toastService } from "@/services/toastService";
import store from "@/store/index"; 
import { DiseaseTree } from "@/entities/diseaseParameters/DiseaseTree";
import { DiseaseTreeItem } from "@/entities/diseaseParameters/DiseaseTreeItem";
import DoctorSelectionModal from "@/components/DoctorSelectionModal.vue";
import { analytics } from '@/services/analytics';

@Component({
  components: {
    DoctorSelectionModal
  }
})
export default class AppTnmStageSelector extends Vue {
  @Prop()
  branchId: number | null = null;
  private localBranchId: number | null = this.branchId;
  private selectedBranch: any = null;
  
  @Prop()
  hideSafetySelector: boolean = false;
  private localHideSafetySelector: boolean = this.hideSafetySelector;

  @Prop()
  hideTreatmentResultCheckbox: boolean = false;
  private localHideTreatmentResultCheckbox: boolean = this.hideTreatmentResultCheckbox;

  @Prop()
  completeData!: boolean;

  @Prop()
  excludedRecommendations!: {
    id: number;
    name: string;
    approvalPercentage: number;
  }[];

  // Oncology-specific state
  private isOncologyFlow: boolean = false;
  private selectedType: string = "";
  private selectedTumour: string | null = null;
  private selectedNode: string | null = null;
  private selectedMetastases: string | null = null;
  private tnmModel: TnmModelTest[] | null = [];
  
  tumours: string[] = [];
  nodes: string[] = [];
  metastases: string[] = [];
  extraQuestions: {
    id: string;
    parentId: string;
    entries: ResultInfo[];
  }[] = [];

  types = ["NSCLC", "SCLC"];

  // Tree navigation state
  private branchModel: (DiseaseTree) | null = null;
  private currentGroup: any = null;
  private navigationStack: any[] = [];
  private showSelection: boolean = true;
  
  // Doctor modal state
  private showDoctorModal: boolean = false;
  private pendingBranchId: number | null = null;
  private selectedDepartmentType: string = '';
  private showProModal: boolean = false;

  @Watch("localBranchId")
  onLocalBranchIdChanged() {
    this.$emit("update:branchId", this.localBranchId);
  }

  @Watch("branchId")
  onBranchIdChanged() {
    this.localBranchId = this.branchId;
    this.showSelection = !this.branchId;
  }

  @Watch("hideSafetySelector")
  onHideSafetySelectorChanged() {
    this.localHideSafetySelector = this.hideSafetySelector;
  }

  @Watch("hideTreatmentResultCheckbox")
  onHideTreatmentResultCheckboxChanged() {
    this.localHideTreatmentResultCheckbox = this.hideTreatmentResultCheckbox;
  }

  // TNM Watchers
  @Watch("selectedType")
  async onTypeChanged(type: string) {
    this.selectedType = type;

    this.tumours =
      this.tnmModel
        ?.map((m) => m.tumour)
        .filter(AppTnmStageSelector._onlyUnique)
        .sort() || [];

    this.nodes = [];
    this.metastases = [];
    this.selectedTumour = null;
    this.selectedNode = null;
    this.selectedMetastases = null;
    this.extraQuestions = [];
  }

  @Watch("selectedTumour")
  onTumourChanged(tumour: string) {
    this.selectedTumour = tumour;

    this.nodes =
      this.tnmModel
        ?.filter((m) => m.tumour === tumour)
        .map((m) => m.node)
        .filter(AppTnmStageSelector._onlyUnique)
        .sort() || [];
    this.selectedNode = null;
    this.selectedMetastases = null;
    this.hideSafetySelector = false;
    this.extraQuestions = [];
  }

  @Watch("selectedNode")
  onNodeChanged(node: string) {
    this.localBranchId = null;
    this.metastases =
      this.tnmModel
        ?.filter((m) => m.tumour == this.selectedTumour && m.node === node)
        .map((m) => m.metastases)
        .filter(AppTnmStageSelector._onlyUnique)
        .sort() || [];
    this.selectedMetastases = null;
    this.extraQuestions = [];
  }

  @Watch("selectedMetastases")
  onMetastasesChanged(metastases: string) {
    console.log('=== METASTASES CHANGED ===');
    console.log('Selected Metastases:', metastases);
    console.log('Current TNM:', {
      type: this.selectedType,
      tumour: this.selectedTumour,
      node: this.selectedNode,
      metastases: metastases
    });
    
    this.localBranchId = null;
    this.extraQuestions = [];

    if (this.metastases) {
      let result = this.tnmModel?.filter(
        (m) =>
          m.tumour == this.selectedTumour &&
          m.node === this.selectedNode &&
          m.metastases === metastases
      )[0];

      console.log('Filtered TNM result:', result);

      if (!result) {
        console.warn('⚠️ No TNM result found for this combination!');
        //toastService.showWarning('No matching TNM staging found');
        return;
      }

      const resultInfo =
        this.selectedType === "NSCLC"
          ? result.resultInfoNsclc
          : result.resultInfoSclc;
      
      console.log('Result Info:', resultInfo);
      
      if (resultInfo.branchId) {
        console.log('✅ Found branchId:', resultInfo.branchId);
        console.log('Triggering doctor modal...');
        
        // Trigger doctor modal
        this.pendingBranchId = resultInfo.branchId;
        this.selectedDepartmentType = 'ONCOLOGY';
        this.showDoctorModal = true;
        
        this.localHideSafetySelector = resultInfo.hideSafetySelector;
        this.localHideTreatmentResultCheckbox = resultInfo.hideTreatmentResultCheckbox;
      } else if (resultInfo.childList.length) {
        console.log('➡️ Has child list, adding extra questions...');
        let result = this.extraQuestions.find((q) => q.id === resultInfo.id);
        if (!result) {
          this.extraQuestions.push({
            id: `${this.selectedTumour}${this.selectedMetastases}${this.metastases}`,
            parentId: resultInfo.id,
            entries: resultInfo.childList,
          });
        } else {
          result.entries = resultInfo.childList;
        }
        console.log('Extra questions:', this.extraQuestions);
      } else {
        console.warn('⚠️ Result has no branchId and no childList');
      }
    }
    console.log('=========================');
  }

  get selectedPathLabels(): string[] {
    const labels: string[] = [];
    
    if (this.isOncologyFlow) {
      // For Oncology, show TNM staging path
      if (this.selectedType) labels.push(this.selectedType);
      if (this.selectedTumour) labels.push(this.selectedTumour);
      if (this.selectedNode) labels.push(this.selectedNode);
      if (this.selectedMetastases) labels.push(this.selectedMetastases);
    } else if (this.selectedBranch) {
      // For tree navigation, show navigation stack path
      for (const item of this.navigationStack) {
        if (item.parameterText) {
          labels.push(item.parameterText);
        }
      }
      labels.push(this.selectedBranch.parameterText);
    }
    
    return labels;
  }

  private getIconPath(iconPath: string): string {
    const iconName = iconPath.replace(/\.(png|jpg|jpeg|svg)$/, '');
    
    try {
      const url = require(`@/assets/icons/${iconName}.png`);
      return url;
    } catch (error) {
      console.error('Icon not found:', iconPath, error);
      return require('@/assets/icons/stethoscope.png');
    }
  }

  private isPremiumItem(item: any): boolean {
    return item.isPremium === true;
  }

  private isPremiumAccessible(item: any): boolean {
    return item.isPremium === true && item.isViewable === true;
  }

  private isPremiumLocked(item: any): boolean {
    return item.isPremium === true && item.isViewable !== true;
  }

  private handleOptionClick(item: any): void {
    const isPremium = item.isPremium === true;
    const isViewable = item.isViewable === true;

    if (!isPremium) {
      this.onGroupItemClick(item);
      return;
    }

    if (isPremium && isViewable) {
      this.onGroupItemClick(item);
      return;
    }

    if (isPremium && !isViewable) {
      this.showProModal = true;
      return;
    }
  }

  private onGroupItemClick(item: any): void {
    console.log('=== ON GROUP ITEM CLICK DEBUG ===');
    console.log('Item:', item.parameterText);
    console.log('Type:', item.type);
    
    // Check if this is "Lung Cancer" specifically (case-insensitive)
    const isLungCancer = (item.parameterText || '').toString().toLowerCase().trim() === 'lung cancer';
    
    console.log('Is Lung Cancer?:', isLungCancer);
    
    // If it's Lung Cancer, switch to TNM flow
    if (isLungCancer) {
      console.log('🔬 LUNG CANCER detected - switching to TNM flow');
      this.enterOncologyFlow(item);
      return;
    }
    
    // Handle non-lung-cancer items
    if (item.type === 'group') {
      console.log('✅ Navigating into GROUP');
      if (this.navigationStack[this.navigationStack.length - 1]?.id !== item.id) {
        this.navigationStack.push(item);
      }
      this.currentGroup = item;
      this.selectedBranch = null;
    } else if (item.type === 'branch') {
      console.log('✅ Selecting BRANCH - showing doctor modal');
      this.pendingBranchId = item.branchId;
      this.selectedDepartmentType = item.departmentType || '';
      this.selectedBranch = item;
      this.showDoctorModal = true;
    }
    console.log('=================================');
  }

  private async enterOncologyFlow(item: any): Promise<void> {
    // Store the oncology branch for later
    this.selectedBranch = item;
    this.pendingBranchId = item.branchId;
    this.selectedDepartmentType = 'ONCOLOGY';
    
    // Load TNM data
    await this.loadTnmData();
    
    // Switch to oncology flow
    this.isOncologyFlow = true;
  }

  private exitOncologyFlow(): void {
    // Reset TNM selections
    this.selectedType = "";
    this.selectedTumour = null;
    this.selectedNode = null;
    this.selectedMetastases = null;
    this.extraQuestions = [];
    
    // Go back to tree navigation
    this.isOncologyFlow = false;
    this.selectedBranch = null;
    this.pendingBranchId = null;
  }

  private selectResult(parentId: any, itemId: string, index: number): void {
    console.log('=== SELECT RESULT DEBUG ===');
    console.log('Parent ID:', parentId);
    console.log('Item ID:', itemId);
    console.log('Index:', index);
    
    this.extraQuestions = this.extraQuestions.filter(
      (q) => q.parentId !== "" && q.parentId !== parentId
    );

    let parent = this.extraQuestions.find((q) => q.id === parentId);
    console.log('Found parent:', parent);
    
    if (parent) {
      let child = parent.entries.find((q) => q.id === itemId);
      console.log('Found child:', child);
      
      if (!child) {
        console.warn('⚠️ Child not found!');
        return;
      }
      
      if (child.branchId) {
        console.log('✅ Found branchId:', child.branchId);
        console.log('Triggering doctor modal...');
        
        // Trigger doctor modal with the branchId
        this.pendingBranchId = child.branchId;
        this.selectedDepartmentType = 'ONCOLOGY';
        this.showDoctorModal = true;
        this.localHideSafetySelector = child.hideSafetySelector;
      } else if (child.id && child.childList.length) {
        console.log('➡️ Has child list, adding more questions...');
        this.localBranchId = null;
        let result = this.extraQuestions.find((q) => q.id === itemId);
        if (!result) {
          this.extraQuestions.push({
            id: child.id,
            parentId: parentId,
            entries: child.childList,
          });
        } else {
          result.entries = child.childList;
        }
      } else {
        console.warn('⚠️ Child has no branchId and no childList');
      }
    } else {
      console.warn('⚠️ Parent not found in extraQuestions!');
    }
    console.log('=========================');
  }

  private goBackInGroup(): void {
    this.selectedBranch = null; 
    this.navigationStack.pop();
    const previous = this.navigationStack[this.navigationStack.length - 1];

    if (previous) {
      this.currentGroup = previous;
    } else {
      this.currentGroup = this.branchModel?.rootItem || null;
    }
  }

  private closeProModal(): void {
    this.showProModal = false;
  }

  editTnm() {
    window.location.reload();
  }

  async created() {
    this.localBranchId = null;
    await this.getDiseaseTree();
    this.$store.dispatch('localQuestionsTreatment/clearLocalQuestionsAndSetInitialized');
  }

  async loadTnmData() {
    console.log('📊 Loading TNM data...');
    let httpHandler = new HttpHandler(store);
    // Always fetch full matrix (no filters initially)
    this.tnmModel = await httpHandler.getToolMatrix(null, null, null);
    console.log('TNM Model loaded:', this.tnmModel?.length, 'entries');
    
    if (!this.tnmModel || this.tnmModel.length === 0) {
      console.warn('⚠️ No TNM data loaded from API');
     // toastService.showWarning('No TNM staging data available');
    }
  }

  async getDiseaseTree() {
    const httpHandler = new HttpHandler(store);
    const tree = await httpHandler.getDiseaseTree(false);
    if (tree) {
      this.branchModel = tree;
      this.currentGroup = tree.rootItem;
      this.navigationStack = [];
    } 
  }

  async onDoctorSelected(selection: { doctorType: string, branchId: number }) {
    try {
      const httpHandler = new HttpHandler(store);

      const startResponse = await httpHandler.startQuestionnaire(selection.branchId, selection.doctorType);

      if (startResponse.success && startResponse.data?.identifier) {
        this.$store.commit('questionnaire/SET_QUESTIONNAIRE_IDENTIFIER', startResponse.data.identifier);
      }

      const branch = await httpHandler.getBranchById(selection.branchId);
      analytics.trackBranchSelected(selection.branchId, branch.name);

      await httpHandler.getToolsByBranchId(selection.branchId, false);

      this.localBranchId = selection.branchId;
      this.showSelection = false;
      this.showDoctorModal = false;
      this.localHideSafetySelector = false;

      this.extraQuestions = [];
      this.$store.dispatch('clearAllTreatments');
      this.$store.dispatch('localQuestionsTreatment/clearLocalQuestionsAndSetInitialized');

      this.$emit("update:completeData", true);
      this.$emit("update:excludedRecommendations", []);
      this.$emit("update:branchId", selection.branchId);

      toastService.showSuccess('Digital Twin selected successfully');
      
    } catch (error) {
      console.error('Error starting questionnaire:', error);
      toastService.showError('Failed to start questionnaire. Please try again.');
      this.showDoctorModal = false;
    }
  }

  closeDoctorModal() {
    this.showDoctorModal = false;
    this.pendingBranchId = null;
    this.selectedDepartmentType = '';
    if (!this.localBranchId) {
      this.selectedBranch = null;
    }
  }

  mounted() {
    this.$events.on("goToMainBranch", this.goToSeperateBranch);
  }
  
  beforeDestroy() {
    this.$events.off("goToMainBranch");
  }

  goToSeperateBranch(toolBranchId: number) {
    const targetItem = this.findBranchById(this.branchModel?.rootItem, toolBranchId);
    if (!targetItem) {
      console.warn(`Branch with ID ${toolBranchId} not found`);
      return;
    }

    // Check if this is an ONCOLOGY branch
    if (targetItem.departmentType === 'ONCOLOGY') {
      this.enterOncologyFlow(targetItem);
    } else {
      this.pendingBranchId = toolBranchId;
      this.selectedDepartmentType = targetItem.departmentType || '';
      this.showDoctorModal = true;
      
      this.selectedBranch = {
        parameterText: targetItem.parameterText,
        branchId: toolBranchId,
        type: 'branch',
      };
    }
  }

  private findBranchById(item: DiseaseTreeItem | undefined, branchId: number): DiseaseTreeItem | null {
    if (!item) return null;

    if (item.type === 'branch' && item.branchId === branchId) {
      return item;
    }

    for (const child of item.nextParameterItems) {
      const found = this.findBranchById(child, branchId);
      if (found) return found;
    }

    return null;
  }

  private static _onlyUnique(value: string, index: number, self: string[]) {
    return self.indexOf(value) === index;
  }
}
</script>




<style scoped lang="scss">
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');

/* Inline header for single-line layout */
.tnm-section-header-inline {
  display: flex;
  align-items: center;
  gap: 2rem;

  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
}

.tnm-label-group {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-shrink: 0;

  min-width: 200px;
}

.tnm-button-group {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

/* Keep original header for extra questions */
.tnm-section-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.extra-questions-single {
  margin-top: 1rem;
  
  .tnm-button-group {
    margin-top: 0.75rem;
  }
}
/* Add TNM-specific styles */

.tnm-single-screen {
  display: flex;
  flex-direction: column;
  //gap: 2rem;
  padding: 1rem 0;
}

.tnm-section-single {
  background: rgba(248, 250, 252, 0.5);
  border-radius: 12px;
  padding: 1.5rem;
  border: 1px solid rgba(226, 232, 240, 0.6);
  transition: all 0.3s ease;
}

.tnm-section-single.tnm-disabled {
  opacity: 0.5;
  pointer-events: none;
}

.tnm-section-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.step-badge {
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, #2f45a5, #667bf2);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.9rem;
  flex-shrink: 0;
}

.step-badge.badge-disabled {
  background: #cbd5e1;
}

.tnm-step-title {
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
}

.tnm-button-group {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.tnm-option-btn {
  padding: 0.75rem 1.5rem;
  background: white;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  color: #475569;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 80px;
}

.tnm-option-btn:hover:not(:disabled) {
  border-color: #667bf2;
  background: rgba(102, 123, 242, 0.05);
  transform: translateY(-1px);
}

.tnm-option-btn.tnm-selected {
  background: linear-gradient(135deg, #2f45a5, #667bf2);
  color: white;
  border-color: #2f45a5;
  box-shadow: 0 4px 12px rgba(47, 69, 165, 0.2);
}

.tnm-option-btn:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.extra-questions-single {
  margin-top: 1rem;
}

.extra-question-label {
  font-size: 0.9rem;
  color: #64748b;
  margin-bottom: 0.75rem;
  font-weight: 500;
}

.tnm-summary {
  background: linear-gradient(135deg, rgba(102, 123, 242, 0.05), rgba(47, 69, 165, 0.02));
  border-radius: 12px;
  padding: 1.5rem;
  border: 1px solid rgba(102, 123, 242, 0.15);
  margin-top: 1rem;
}

.summary-header h5 {
  font-size: 1rem;
  font-weight: 600;
  color: #2f45a5;
  margin-bottom: 1rem;
}

.summary-path {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
}

.summary-badge {
  background: linear-gradient(135deg, #2f45a5, #667bf2);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.9rem;
  box-shadow: 0 2px 8px rgba(47, 69, 165, 0.15);
}

.tnm-selector-wrapper {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

/* ===== WELCOME HEADER ===== */
.welcome-header {
  text-align: center;
  //margin-bottom: 1.5rem;
  padding: 1rem 1rem 1rem 1.5rem;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(248, 250, 252, 0.9));
  border-radius: 20px;
  border: 1px solid rgba(102, 123, 242, 0.15);
  box-shadow: 0 4px 16px rgba(47, 69, 165, 0.06);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #2f45a5, #667bf2, #10b981);
  }
}

.welcome-text {
  font-size: 1.3rem;
  font-weight: 600;
  color: #64748b;
  margin: 0 0 0.75rem 0;
  letter-spacing: 0.5px;
}

.logo-container {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 0.75rem;
}

.welcome-logo {
  height: 70px;
  width: auto;
  max-width: 100%;
  object-fit: contain;
  filter: drop-shadow(0 2px 8px rgba(47, 69, 165, 0.1));
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.02);
  }
}

.welcome-title {
  font-size: 2.5rem;
  font-weight: 900;
  background: linear-gradient(135deg, #2f45a5, #667bf2);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0 0 0.75rem 0;
  letter-spacing: -0.5px;
}

.welcome-subtitle {
  font-size: 1.0625rem;
  color: #475569;
  margin: 0;
  font-weight: 500;
  line-height: 1.5;
  max-width: 600px;
  margin: 0 auto;
}

/* ===== PREMIUM STYLING - CRITICAL SECTION ===== */

/* Make option-button position relative for absolute positioning of badges */
.option-button {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 1.5rem 1rem;
  background: white;
  border: 2px solid rgba(102, 123, 242, 0.2);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: center;

  &:not(.option-disabled):hover {
    border-color: #2f45a5;
    transform: translateY(-4px);
    box-shadow: 0 8px 20px rgba(47, 69, 165, 0.15);
  }

  &.option-branch:not(.option-disabled) {
    border-color: rgba(16, 185, 129, 0.3);

    &:hover {
      border-color: #10b981;
      box-shadow: 0 8px 20px rgba(16, 185, 129, 0.15);
    }

    .option-icon {
      background: linear-gradient(135deg, #10b981, #34d399);
    }
  }
}

/* ✅ PREMIUM ACCESSIBLE STYLING - Looks clickable and inviting */
.option-button.option-premium-accessible {
  border-color: rgba(16, 185, 129, 0.4) !important;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.02), rgba(255, 255, 255, 0.98)) !important;
  opacity: 0.5;
  
  &:hover {
    border-color: #10b981 !important;
    box-shadow: 0 8px 25px rgba(16, 185, 129, 0.2) !important;
    transform: translateY(-4px) !important;
  }

  .option-icon {
    background: linear-gradient(180deg, #059669, #6ee7b7) !important;
  }
}

/* 🔒 PREMIUM LOCKED STYLING - Grayed out and not clickable */
.option-button.option-disabled {
  opacity: 0.5 !important;
  cursor: not-allowed !important;
  background: rgba(248, 250, 252, 0.8) !important;
  border-color: rgba(226, 232, 240, 0.6) !important;
  
  &:hover {
    transform: none !important;
    box-shadow: none !important;
    border-color: rgba(226, 232, 240, 0.6) !important;
  }

  .option-icon {
    background: #64748b !important;
  }

  .option-text {
    color: #94a3b8 !important;
  }
}

/* ✅ PRO Badge for ACCESSIBLE items - Green to show it's available */
.pro-badge {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  background: linear-gradient(135deg, #fbbf24, #f59e0b);
  color: white;
  font-size: 0.65rem;
  font-weight: 700;
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  z-index: 10;
  pointer-events: none;
  box-shadow: 0 2px 8px rgba(245, 158, 11, 0.3);
}

/* ✅ Green badge for accessible premium items */
.pro-badge.pro-badge-accessible {
  background: linear-gradient(135deg, #10b981, #34d399) !important;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3) !important;
}

/* 🔒 Lock Icon - Only shows for LOCKED items */
.lock-icon {
  position: absolute;
  bottom: 0.75rem;
  right: 0.75rem;
  width: 20px;
  height: 20px;
  color: #cbd5e1;
  pointer-events: none;
  z-index: 5;
}

/* ===== REST OF THE STYLES ===== */

/* Selected Path Section (Breadcrumb) */
.selected-path-section {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  margin-bottom: 1.5rem;
}

.path-header {
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.section-title {
  font-size: 1.5rem;
  font-weight: 800;
  color: #1e293b;
  margin: 0;
}

.breadcrumb-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.breadcrumb-path {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  flex: 1;
}

.breadcrumb-item {
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.4rem;
  background: linear-gradient(135deg, #10b981, #34d399);
  color: white;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.2);
}

.breadcrumb-divider {
  margin: 0 -4px;
  width: 16px;
  height: 16px;
  color: #cbd5e1;
  flex-shrink: 0;
}

.btn-edit,
.btn-backd {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1rem;
  background: rgba(102, 123, 242, 0.1);
  color: #2f45a5;
  border: 2px solid rgba(102, 123, 242, 0.2);
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.3s ease;

  svg {
    width: 18px;
    height: 18px;
  }

  &:hover {
    background: rgba(102, 123, 242, 0.15);
    border-color: #2f45a5;
    transform: translateY(-1px);
  }
}

/* Navigation Card */
.navigation-card {
  background: linear-gradient(135deg, rgba(248, 250, 252, 0.8), rgba(241, 245, 255, 0.6));
  border-radius: 16px;
  padding: 2rem;
  border: 1px solid rgba(226, 232, 240, 0.5);
}

.selection-title {
  font-size: 1.2rem;
  font-weight: 600;
  background: linear-gradient(135deg, #2f45a5, #667bf2);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 0.5rem;
}

.selection-subtitle {
  color: #64748b;
  font-size: 1rem;
  margin: 0 0 2rem 0;
}

.card-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  gap: 1rem;
}

.current-step-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
}

.step-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
}

.btn-back,
.btn-backd {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  background: rgba(102, 123, 242, 0.1);
  color: #2f45a5;
  border: 2px solid rgba(102, 123, 242, 0.2);
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;

  svg {
    width: 18px;
    height: 18px;
  }

  &:hover {
    background: rgba(102, 123, 242, 0.15);
    border-color: #2f45a5;
    transform: translateX(-2px);
  }
}

/* Options Grid */
.options-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 1rem;
}

.option-icon {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #2f45a5, #667bf2);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  img {
    width: 28px;
    height: 28px;
    filter: brightness(0) invert(1);
  }

  svg {
    width: 24px;
    height: 24px;
    color: white;
  }
}

.option-text {
  font-size: 0.95rem;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.4;
  word-break: break-word;
}

/* Pro Modal Styles */
.pro-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.pro-modal {
  background: white;
  border-radius: 20px;
  max-width: 480px;
  width: 100%;
  padding: 2.5rem;
  position: relative;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: modalFadeIn 0.3s ease-out;
}

@keyframes modalFadeIn {
  from {
    opacity: 0;
    transform: scale(0.95) translateY(10px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.modal-close {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: transparent;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 8px;
  transition: all 0.2s;

  &:hover {
    background: rgba(148, 163, 184, 0.1);
    color: #64748b;
  }
}

.modal-icon {
  width: 64px;
  height: 64px;
  background: linear-gradient(135deg, #2f45a5, #667bf2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;

  svg {
    width: 32px;
    height: 32px;
    color: white;
  }
}

.modal-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #1e293b;
  text-align: center;
  margin-bottom: 0.75rem;
}

.modal-subtitle {
  color: #64748b;
  text-align: center;
  margin-bottom: 1.5rem;
  line-height: 1.6;
}

.modal-features {
  background: linear-gradient(135deg, rgba(241, 245, 249, 0.8), rgba(248, 250, 252, 0.6));
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
}

.feature-item {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.75rem;

  &:last-child {
    margin-bottom: 0;
  }
}

.feature-check {
  width: 20px;
  height: 20px;
  color: #10b981;
  flex-shrink: 0;
  margin-top: 0.125rem;
}

.feature-text {
  color: #475569;
  font-size: 0.95rem;
  line-height: 1.5;
}

.btn-upgrade {
  width: 100%;
  background: linear-gradient(135deg, #2f45a5, #667bf2);
  color: white;
  border: none;
  padding: 1rem;
  border-radius: 10px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(47, 69, 165, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(47, 69, 165, 0.4);
  }
}

.btn-later {
  width: 100%;
  background: transparent;
  color: #64748b;
  border: none;
  padding: 0.75rem;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 0.75rem;

  &:hover {
    color: #475569;
    background: rgba(100, 116, 139, 0.05);
  }
}
/* Take a Tour Button */
.btn-take-tour {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 14px 28px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
  position: relative;
  overflow: hidden;
}

.btn-take-tour::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.5s;
}

.btn-take-tour:hover::before {
  left: 100%;
}

.btn-take-tour:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
}

.btn-take-tour:active {
  transform: translateY(0);
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
}

.tour-icon {
  flex-shrink: 0;
  animation: pulse 2s ease-in-out infinite;
}

.arrow-icon {
  flex-shrink: 0;
  transition: transform 0.3s;
}

.btn-take-tour:hover .arrow-icon {
  transform: translateX(4px);
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .welcome-header {
    padding: 1.5rem 1.25rem 1.25rem 1.25rem;
    margin-bottom: 1.25rem;
  }

  .welcome-text {
    font-size: 1.125rem;
    margin-bottom: 0.5rem;
  }

  .welcome-logo {
    height: 56px;
  }

  .logo-container {
    margin-bottom: 0.5rem;
  }

  .welcome-title {
    font-size: 1.875rem;
  }

  .welcome-subtitle {
    font-size: 1rem;
  }

  .btn-backd {
    display: inline-flex;
    padding: 0.5rem;
    width: 36px;
    height: 36px;
    justify-content: center;
    border-radius: 8px;
  }
  
  .btn-edit {
    display: none;
  }

  .selected-path-section,
  .navigation-card {
    padding: 1rem 1.25rem;
  }

  .section-title {
    font-size: 1.2rem;
  }

  .path-header {
    margin-bottom: 0.75rem;
  }

  .breadcrumb-container {
    flex-direction: row;
    align-items: center;
    flex-wrap: wrap;
  }

  .selection-title {
    font-size: 1.1rem;
  }

  .card-header-row {
    flex-direction: row;
    align-items: center;
  }

  .btn-back {
    width: auto;
    padding: 0.5rem;
    width: 36px;
    height: 36px;
    justify-content: center;
    flex-shrink: 0;
  }

  .btn-back-label {
    display: none;
  }

  .btn-backd {
    width: auto;
    align-self: flex-start;
  }

  .options-grid {
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: 0.75rem;
  }

  .option-button {
    padding: 1.25rem 0.75rem;
  }

  .option-icon {
    width: 40px;
    height: 40px;

    img,
    svg {
      width: 20px;
      height: 20px;
    }
  }

  .option-text {
    font-size: 0.875rem;
  }

  .pro-modal {
    padding: 2rem;
    margin: 1rem;
  }

  .modal-title {
    font-size: 1.5rem;
  }
}

@media (min-width: 769px) {
  .btn-backd {
    display: none;
  }
  
  .btn-edit {
    display: inline-flex;
  }
}

@media (max-width: 480px) {
  .welcome-header {
    padding: 1.25rem 1rem 1rem 1rem;
    margin-bottom: 1rem;
    border-radius: 16px;
  }

  .welcome-text {
    font-size: 1rem;
    margin-bottom: 0.5rem;
  }

  .welcome-logo {
    height: 48px;
  }

  .logo-container {
    margin-bottom: 0.5rem;
  }

  .welcome-title {
    font-size: 1.5rem;
  }

  .welcome-subtitle {
    font-size: 0.9375rem;
  }

  .selected-path-section,
  .navigation-card {
    padding: 0.875rem 1rem;
    border-radius: 12px;
  }

  .section-title {
    font-size: 1.1rem;
  }

  .path-header {
    margin-bottom: 0.5rem;
  }

  .breadcrumb-item {
    font-size: 0.78rem;
    padding: 0.3rem 0.6rem;
  }

  .selection-title {
    font-size: 1.1rem;
  }

  .selection-subtitle {
    font-size: 0.875rem;
  }

  .step-title {
    font-size: 1.125rem;
  }

  .breadcrumb-item {
    font-size: 0.8125rem;
    padding: 0.4rem 0.75rem;
  }

  .options-grid {
    grid-template-columns: 1fr;
  }

  .breadcrumb-path {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0.5rem;
    align-items: stretch;
  }

  .breadcrumb-divider {
    display: none;
  }

  .breadcrumb-item {
    width: 100%;
    text-align: left;
    align-items: flex-start;
    padding: 0.5rem 0.75rem;
    border-radius: 10px;
  }
}
</style>